import type { BlocksContent } from '@strapi/blocks-react-renderer';
import type {
  HeroCardOnlineTabConfig,
  HeroCardPostCtaResolved,
  HeroCardOnlineTabResolved,
  HeroCardPricesConfig,
  HeroCardPricesResolved,
  HeroCardSearchCenteredConfig,
  HeroCardSearchCenteredResolved,
  HeroCardSearchResolved,
  HeroCardTabbedCityConfig,
  HeroCardTabbedCityResolved,
  HeroCardTabbedHomeConfig,
  HeroCardTabbedHomeResolved,
  HeroCardTextConfig,
  HeroCardTextResolved,
  PreselectedTab,
} from './types';

// The hero cards bypass `SlotRenderer` and so have no `resolveSync` hook of
// their own (they are not `WIDGET_REGISTRY` entries). These normalisers play
// that role: they collapse the CMS's "every field may be absent" shape into
// one where the renderers never branch on undefined. Pure, so unit-tested
// directly rather than through the components.

/** An empty `blocks` field arrives as `undefined` or `[]`; both mean "no body". */
function body(value: BlocksContent | undefined): BlocksContent | null {
  return value && value.length > 0 ? value : null;
}

function text(value: string | undefined): string {
  return value ?? '';
}

function preselectedTab(value: PreselectedTab | undefined): PreselectedTab {
  // Anything other than the two enum members (including a value written before
  // the enum existed) opens tab 1, matching the schema default.
  return value === 'two' ? 'two' : 'one';
}

/**
 * Normalises one tab's post-CTA group. `prefix` is the tab it belongs to, so
 * the same reader serves `tabOne*` and `tabTwo*` on both tabbed cards.
 */
function postCta(
  config: Record<string, unknown>,
  prefix: 'tabOne' | 'tabTwo',
): HeroCardPostCtaResolved {
  const at = (suffix: string) => config[`${prefix}PostCta${suffix}`];
  return {
    text: body(at('Text') as BlocksContent | undefined),
    phone: text(at('Phone') as string | undefined),
    matelsoPhoneLabel: text(at('MatelsoPhoneLabel') as string | undefined),
    linkLabel: text(at('LinkLabel') as string | undefined),
    linkAnchor: text(at('LinkAnchor') as string | undefined),
  };
}

export function resolveHeroCardText(
  config: HeroCardTextConfig,
): HeroCardTextResolved {
  return {
    title: text(config.title),
    body: body(config.body),
    ctas: config.ctas ?? [],
    phone: text(config.phone),
    matelsoPhoneLabel: text(config.matelsoPhoneLabel),
  };
}

/**
 * Tab 2 is the same three fields on both tabbed cards (SH-312 O6), so one
 * normaliser serves both and the two cards cannot drift apart.
 */
export function resolveHeroCardOnlineTab(
  config: HeroCardOnlineTabConfig,
): HeroCardOnlineTabResolved {
  return {
    label: text(config.tabTwoLabel),
    anchor: text(config.tabTwoAnchor),
    body: body(config.tabTwoBody),
    ctas: config.tabTwoCtas ?? [],
    postCta: postCta(config as Record<string, unknown>, 'tabTwo'),
  };
}

export function resolveHeroCardTabbedHome(
  config: HeroCardTabbedHomeConfig,
): HeroCardTabbedHomeResolved {
  const search: HeroCardSearchResolved = {
    placeholder: text(config.tabOneSearchPlaceholder),
    buttonLabel: text(config.tabOneSearchButtonLabel),
    anchor: text(config.tabOneSearchAnchor),
    tracking: config.tabOneSearchTracking ?? [],
  };
  return {
    tabOneLabel: text(config.tabOneLabel),
    tabOneAnchor: text(config.tabOneAnchor),
    tabOneTitle: text(config.tabOneTitle),
    tabOneSearch: search,
    tabOneBody: body(config.tabOneBody),
    tabOneCtas: config.tabOneCtas ?? [],
    tabOnePostCta: postCta(config as Record<string, unknown>, 'tabOne'),
    tabTwo: resolveHeroCardOnlineTab(config),
    preselectedTab: preselectedTab(config.preselectedTab),
  };
}

export function resolveHeroCardTabbedCity(
  config: HeroCardTabbedCityConfig,
): HeroCardTabbedCityResolved {
  return {
    tabOneLabel: text(config.tabOneLabel),
    tabOneAnchor: text(config.tabOneAnchor),
    tabOneTitle: text(config.tabOneTitle),
    tabOneBody: body(config.tabOneBody),
    tabOneCtas: config.tabOneCtas ?? [],
    tabOnePostCta: postCta(config as Record<string, unknown>, 'tabOne'),
    tabTwo: resolveHeroCardOnlineTab(config),
    preselectedTab: preselectedTab(config.preselectedTab),
  };
}

export function resolveHeroCardSearchCentered(
  config: HeroCardSearchCenteredConfig,
): HeroCardSearchCenteredResolved {
  return {
    body: body(config.body),
    search: {
      placeholder: text(config.searchPlaceholder),
      buttonLabel: text(config.searchButtonLabel),
      anchor: text(config.searchAnchor),
      tracking: config.searchTracking ?? [],
    },
  };
}

export function resolveHeroCardPrices(
  config: HeroCardPricesConfig,
): HeroCardPricesResolved {
  return {
    title: text(config.title),
    body: body(config.body),
    search: {
      placeholder: text(config.searchPlaceholder),
      buttonLabel: text(config.searchButtonLabel),
      anchor: text(config.searchAnchor),
      tracking: config.searchTracking ?? [],
    },
    callCtaText: text(config.callCtaText),
    phone: text(config.phone),
    matelsoPhoneLabel: text(config.matelsoPhoneLabel),
  };
}
